import { Button, Card, Group, Stack, Text, TextInput } from "@mantine/core";
import { IconDownload, IconExternalLink, IconSearch } from "@tabler/icons-react";
import { useState } from "react";

import { reportUrl, type ReportFile } from "@/api/client";

/**
 * The files in a project's download/ folder (Reports tab).
 *
 * Built for supervisors on Android phones: one stacked card per report, big
 * buttons, and a filter so a supervisor can type their region instead of
 * scrolling past everyone else's. View opens the report in a new tab; Download
 * saves it to the phone for reading offline.
 */
export function ReportsPanel({
  reports,
  illegal,
  slug,
}: {
  reports: ReportFile[];
  /** Files refused for their name; each gets a red line at the bottom. */
  illegal: string[];
  slug: string;
}) {
  const [query, setQuery] = useState("");

  if (reports.length === 0) {
    return (
      <Stack gap="md">
        <Text c="dimmed">No reports have been published for this project yet.</Text>
        <IllegalNames names={illegal} />
      </Stack>
    );
  }

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? reports.filter((report) => report.name.toLowerCase().includes(needle))
    : reports;

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Filter reports"
        leftSection={<IconSearch size={16} />}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        aria-label="Filter reports"
      />

      {shown.length === 0 && (
        <Text c="dimmed">No reports match “{query.trim()}”.</Text>
      )}

      {shown.map((report) => (
        <Card key={report.filename} withBorder padding="md">
          <Group justify="space-between" align="center" gap="sm">
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Text fw={600} style={{ overflowWrap: "anywhere" }}>
                {report.name}
              </Text>
              <Text size="sm" c="dimmed">
                Updated {formatModified(report.modified)} · {formatBytes(report.size_bytes)}
              </Text>
            </Stack>
            <Group gap="xs">
              {report.viewable && (
                <Button
                  component="a"
                  href={reportUrl(slug, report.filename)}
                  target="_blank"
                  rel="noopener"
                  leftSection={<IconExternalLink size={16} />}
                >
                  View
                </Button>
              )}
              <Button
                component="a"
                href={reportUrl(slug, report.filename, true)}
                download={report.filename}
                variant="light"
                leftSection={<IconDownload size={16} />}
              >
                Download
              </Button>
            </Group>
          </Group>
        </Card>
      ))}

      <Text size="sm" c="dimmed">
        To read a report offline, tap Download. It is saved to your phone's Downloads
        folder; open it from there in Chrome.
      </Text>

      <IllegalNames names={illegal} />
    </Stack>
  );
}

/** Files in download/ the server refuses for their name (a "+" or "%"). */
function IllegalNames({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <Stack gap={2}>
      {names.map((name) => (
        <Text key={name} size="sm" c="red">
          Error--Illegal Report filename: {name}
        </Text>
      ))}
    </Stack>
  );
}

function formatModified(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
